import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getIXGradesForCourse } from "../../../redux/studentAccommodations/studentAccommodationsActions";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  useMediaQuery,
} from "@material-ui/core";
import { Grid as MuiGrid } from "@mui/material";
import ClassButton from "../../dashboard/components/currentCourses/ClassButton";
import CalendarIcon from "../../../images/CalendarIcon.svg";
import styles from "../styles";
import CourseTableRow from "./CourseTableRow";
import { useTheme, createTheme, ThemeProvider } from "@mui/material/styles";

function CourseTableCard(props) {
  const useStyles = styles;
  const classes = useStyles();
  const pageTheme = useTheme();
  const matches = useMediaQuery(pageTheme.breakpoints.up("md"));
  const toggleDatabaseDate = new Date(process.env.REACT_APP_TOGGLE_DATE);
  const theme = createTheme();

  // Fetch IX grades once per course
  useEffect(() => {
    if (props.course.courseOfferingId) {
      props.getIXGradesForCourse(props.course.courseOfferingId);
    }
  }, [props.course.courseOfferingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get IX grades state from Redux
  const ixGradesState = props.ixGrades?.[props.course.courseOfferingId];
  
  // Show error snackbar if IX grades failed to load
  useEffect(() => {
    if (ixGradesState?.error) {
      console.error("Failed to fetch IX grades:", ixGradesState.error);
    }
  }, [ixGradesState?.error]);

  const getMonthString = (month) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return months[month - 1];
  };

  const updateDateFormat = (date) => {
    if (props.course.offeringType !== "WS" || !props.wsUser) {
      const [month, day, year] = date.split("/");
      return `${getMonthString(month)} ${day}, ${year}`;
    } else {
      const [year, month, day] = date.split("-");
      return `${getMonthString(month)} ${day}, ${year}`;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <MuiGrid
        container
        className={classes.tableHeaderContainer}
        component={Paper}
        elevation={2}
        xs={matches ? 11.15 : 11.5}
        style={{
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <Grid className={classes.tableHeaderText}>
          <Box>
            <Typography className={classes.courseText} component="h2">
              {`${props.course.courseId}: ${props.course.courseTitle}`}
            </Typography>
            <Typography>{`Group ID: ${props.course.groupId}`}</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                margin: "4px 0px 0px 0px",
              }}
            >
              <img
                src={CalendarIcon}
                alt="Calendar icon showing dates"
                aria-labelledby="calendarIconTitle"
                width="24"
                height="24"
              />
              <Typography
                data-testid="courseDuration"
                variant="body2"
                className={classes.dateText}
              >
                {`${updateDateFormat(
                  props.course.startDate
                )} - ${updateDateFormat(props.course.endDate)}`}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Box
          className={
            matches
              ? classes.classButtonContainer
              : classes.classButtonContainerMobile
          }
        >
          <ClassButton
            accommodationsPage={true}
            courseName={`${props.course.courseId}: ${props.course.courseTitle}`}
            courseOfferingId={
              (props.course.offeringType !== "WS"
                ? "OSIRIS:"
                : new Date(props.course.startDate) >= toggleDatabaseDate
                  ? "FWS:"
                  : "SDW:"
              ) + props.course.courseOfferingId         
            }
          />
        </Box>
        <Grid item xs={12} className={classes.tablePadding}>
          <TableContainer
            className={classes.tableContainer}
            style={{
              maxWidth: "1232px",
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table
              style={{
                width: "100%",
                maxWidth: "1232px",
              }}
            >
              <TableHead className={classes.courseCardTableHeader}>
                <TableRow className={classes.tableRow}>
                  <TableCell
                    align="left"
                    className={classes.stickyColumnHeader}
                    style={{ width: "15%", maxWidth: "180px", fontSize: "14px" }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ color: "#ffffff", fontSize: "14px" }}>Student name</span>
                      <span style={{ color: "#ffffff", fontSize: "14px" }}>IRN</span>
                    </div>
                  </TableCell>
                  <TableCell
                    align="left"
                    className={classes.tableText}
                    style={{ width: "12%", maxWidth: "120px", fontSize: "14px" }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    align="left"
                    className={classes.tableText}
                    style={{ width: "23%", maxWidth: "280px", fontSize: "14px" }}
                  >
                    Accommodations
                  </TableCell>
                  <TableCell
                    align="left"
                    className={classes.tableText}
                    style={{ width: "28%", maxWidth: "350px", fontSize: "14px" }}
                  >
                    Notes
                  </TableCell>
                  <TableCell
                    align="left"
                    className={classes.tableText}
                    style={{ width: "12%", maxWidth: "140px", fontSize: "14px" }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <Typography style={{ fontSize: "14px" }}>Final grade</Typography>
                      <Typography style={{ fontSize: "14px" }}>issued</Typography>
                    </div>
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.tableText}
                    style={{ width: "10%", maxWidth: "120px", fontSize: "14px" }}
                  >
                    Action(s)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.course.studentList
                  .map((student) => (
                    <CourseTableRow
                      key={student.studentIrn}
                      student={student}
                      courseId={props.course.courseId}
                      courseStartDate={props.course.startDate}
                      courseEndDate={props.course.endDate}
                      courseOfferingId={props.course.courseOfferingId}
                      setAlreadyAcknowledged={props.setAlreadyAcknowledged}
                      setOpenModal={props.setOpenModal}
                      setModalType={props.setModalType}
                      setActionNeededObject={props.setActionNeededObject}
                      actionNeededObject={props.actionNeededObject}
                      setContactInfoDisabilityType={props.setContactInfoDisabilityType}
                      update={props.update}
                      setUpdate={props.setUpdate}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box style={{ padding: "12px" }} />
        </Grid>
      </MuiGrid>
    </ThemeProvider>
  );
}

const mapDispatchToProps = {
  getIXGradesForCourse,
};

const mapStateToProps = (state) => ({
  ixGrades: state.ixGrades,
});

export default connect(mapStateToProps, mapDispatchToProps)(CourseTableCard);
